import { format } from 'date-fns';
import { getAllRanges } from '../../services/LightweightFilters/LightweightFiltersRangesService';
import type { NewDateRangeInfo } from '../../types/dateRangeTypes';

/*
 * (date-fns equivalent of DEFAULT_DATE_FORMAT)
 * default for how the dashboard stores dates internally (i.e. when the program executes, not what is necessarily displayed to the user).
 *
 * '2018-05-09'
 */
export const DEFAULT_DATE_FORMAT_DATE_FNS = 'yyyy-MM-dd';
export const SHORT_DATE_FORMAT_DATE_FNS = 'MMM dd, yyyy';

export const formatToDefaultDateFormat = (date: Date): string => {
  return format(date, DEFAULT_DATE_FORMAT_DATE_FNS);
};

export const handleGetNewDateRangeInfo = (dateRangeInfo: Partial<NewDateRangeInfo>): NewDateRangeInfo => {
  const allDefaultRanges = getAllRanges();
  const defaultRange = dateRangeInfo.defaultRange || 'custom';

  if (defaultRange === 'custom') {
    const { dateRange } = dateRangeInfo;
    const startDate = dateRange?.start_date || formatToDefaultDateFormat(new Date());
    const endDate = dateRange?.end_date || formatToDefaultDateFormat(new Date());

    return {
      dateRange: {
        start_date: startDate,
        end_date: endDate,
      },
      defaultRange,
    };
  }

  const calculatedDateRange = allDefaultRanges.find((range) => range.type === defaultRange)?.getValue();
  const startDate = formatToDefaultDateFormat((calculatedDateRange && calculatedDateRange[0]) || new Date());
  const endDate = formatToDefaultDateFormat((calculatedDateRange && calculatedDateRange[1]) || new Date());

  return {
    dateRange: {
      start_date: startDate,
      end_date: endDate,
    },
    defaultRange,
  };
};

export const getTimezoneOffset = (timeZone: string): number => {
  const hereDate = new Date(Date.now());
  hereDate.setMilliseconds(0); // for nice rounding

  const hereOffsetHrs = (hereDate.getTimezoneOffset() / 60) * -1;
  const thereLocaleStr = hereDate.toLocaleString('en-US', { timeZone });
  const thereDate = new Date(thereLocaleStr);
  const diffHrs = (thereDate.getTime() - hereDate.getTime()) / 1000 / 60 / 60;
  const thereOffsetHrs = hereOffsetHrs + diffHrs;

  return thereOffsetHrs;
};

export const convertTimestampToDate = (timestamp: string): string => {
  const date = new Date(timestamp);

  const options = { day: '2-digit', month: 'long', year: 'numeric' };

  const formattedDate = date.toLocaleDateString('en-GB', options as any);

  return formattedDate;
};

export const calculateDaysUntilExpiry = (futureExpiry: number): number | undefined => {
  if (isNaN(futureExpiry)) {
    return undefined;
  }

  const currentEpochTime = Date.now();

  const diffInMilliseconds = futureExpiry - currentEpochTime;

  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

  return Math.ceil(diffInDays);
};
